/**
 * FlashPickBanner — Global urgent pick alert with countdown timer.
 * Polls /api/public/flash-pick every 30s. Mounts above everything.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Lightning, X, Lock } from "@phosphor-icons/react";

const POLL_MS = 30_000;

function useFlashPick() {
  const [pick, setPick] = useState(null);
  const timerRef = useRef(null);

  const fetch_ = useCallback(async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${base}/api/public/flash-pick`);
      if (!res.ok) return;
      const { flash_pick } = await res.json();
      setPick(flash_pick);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetch_();
    timerRef.current = setInterval(fetch_, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetch_]);

  return [pick, setPick];
}

function useCountdown(expiresAt) {
  const [secs, setSecs] = useState(0);

  useEffect(() => {
    if (!expiresAt) { setSecs(0); return; }
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
      setSecs(diff);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return secs;
}

function pad(n) { return String(n).padStart(2, "0"); }

function DigitalClock({ secs }) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const urgent = secs <= 300; // last 5 minutes

  return (
    <div className="flex items-center gap-0.5 font-mono" data-testid="flash-countdown">
      {h > 0 && (
        <>
          <ClockSegment value={pad(h)} urgent={urgent} />
          <span className="text-xl font-black pb-1" style={{ color: urgent ? "#EF4444" : "#F97316" }}>:</span>
        </>
      )}
      <ClockSegment value={pad(m)} urgent={urgent} />
      <span className="text-xl font-black pb-1" style={{ color: urgent ? "#EF4444" : "#F97316" }}>:</span>
      <ClockSegment value={pad(s)} urgent={urgent} />
    </div>
  );
}

function ClockSegment({ value, urgent }) {
  return (
    <div
      className="flex items-center justify-center rounded-md font-black text-lg w-10 h-10 tabular-nums"
      style={{
        background: urgent ? "rgba(239,68,68,0.15)" : "rgba(249,115,22,0.12)",
        border: `1px solid ${urgent ? "rgba(239,68,68,0.4)" : "rgba(249,115,22,0.3)"}`,
        color: urgent ? "#EF4444" : "#F97316",
        textShadow: urgent
          ? "0 0 12px rgba(239,68,68,0.8)"
          : "0 0 10px rgba(249,115,22,0.7)",
        boxShadow: urgent
          ? "0 0 16px rgba(239,68,68,0.2) inset"
          : "0 0 12px rgba(249,115,22,0.15) inset",
      }}
    >
      {value}
    </div>
  );
}

export default function FlashPickBanner() {
  const [pick, setPick] = useFlashPick();
  const secs = useCountdown(pick?.flash_expires_at);
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(`flash_dismissed_${pick?.id || ""}`) === "1"
  );

  // Clear dismissal when a new flash pick starts
  const prevId = useRef(null);
  useEffect(() => {
    if (pick?.id && pick.id !== prevId.current) {
      const wasDismissed = sessionStorage.getItem(`flash_dismissed_${pick.id}`) === "1";
      setDismissed(wasDismissed);
      prevId.current = pick.id;
    }
  }, [pick?.id]);

  if (!pick || secs <= 0 || dismissed) return null;

  const urgent   = secs <= 300;
  const isLocked = pick.access_tier !== "free";

  const MARKET_LABELS = {
    "1x2": "Resultado Final", "over_under": "Más/Menos", "moneyline": "Ganador Directo",
    "spread": "Hándicap", "run_line": "Spread", "btts": "Ambos Anotan",
    "double_chance": "Doble Oportunidad",
  };

  return (
    <>
      {/* Global keyframes */}
      <style>{`
        @keyframes flash-pulse {
          0%,100% { opacity: 1; }
          50%      { opacity: 0.85; }
        }
        @keyframes flash-glow {
          0%,100% { box-shadow: 0 0 0 0 rgba(249,115,22,0.5), 0 4px 30px rgba(0,0,0,0.6); }
          50%      { box-shadow: 0 0 0 6px rgba(249,115,22,0), 0 4px 30px rgba(0,0,0,0.6); }
        }
        @keyframes flash-urgent-glow {
          0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.6), 0 4px 30px rgba(0,0,0,0.7); }
          50%      { box-shadow: 0 0 0 8px rgba(239,68,68,0), 0 4px 30px rgba(0,0,0,0.7); }
        }
        @keyframes flash-slide-down {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes flash-bolt-spin {
          0%  { transform: rotate(-10deg) scale(1); }
          50% { transform: rotate(10deg)  scale(1.2); }
          100%{ transform: rotate(-10deg) scale(1); }
        }
        .flash-banner { animation: flash-slide-down 0.4s cubic-bezier(0.16,1,0.3,1) both; }
        .flash-glow-anim { animation: flash-glow 2s ease-in-out infinite; }
        .flash-urgent-anim { animation: flash-urgent-glow 1s ease-in-out infinite; }
        .flash-bolt { animation: flash-bolt-spin 1.8s ease-in-out infinite; }
        .flash-pulse { animation: flash-pulse 1.2s ease-in-out infinite; }
      `}</style>

      <div
        data-testid="flash-pick-banner"
        onClick={() => navigate(`/jugadas/${pick.id}`)}
        className={`flash-banner ${urgent ? "flash-urgent-anim" : "flash-glow-anim"} cursor-pointer select-none`}
        style={{
          position: "relative",
          zIndex: 45,
          background: urgent
            ? "linear-gradient(135deg, #1A0A0A 0%, #200808 50%, #1A0A0A 100%)"
            : "linear-gradient(135deg, #0F0A00 0%, #1A1000 50%, #0F0A00 100%)",
          borderBottom: `2px solid ${urgent ? "rgba(239,68,68,0.6)" : "rgba(249,115,22,0.5)"}`,
          overflow: "hidden",
        }}
      >
        {/* Animated grain texture overlay */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.03,
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")",
          pointerEvents: "none",
        }} />

        {/* Scanning light line */}
        <div
          style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 2,
            background: urgent
              ? "linear-gradient(90deg, transparent 0%, rgba(239,68,68,0.9) 50%, transparent 100%)"
              : "linear-gradient(90deg, transparent 0%, rgba(249,115,22,0.9) 50%, transparent 100%)",
          }}
        />

        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-3 flex items-center gap-4">

          {/* ── Left: Flash badge ───────────────────────── */}
          <div className="flex-shrink-0 flex flex-col items-center gap-1">
            <div
              className="flash-pulse inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-black text-[10px] uppercase tracking-[0.2em]"
              style={{
                background: urgent ? "rgba(239,68,68,0.2)" : "rgba(249,115,22,0.18)",
                border: `1px solid ${urgent ? "rgba(239,68,68,0.5)" : "rgba(249,115,22,0.45)"}`,
                color: urgent ? "#EF4444" : "#F97316",
              }}
            >
              <Lightning weight="fill" className="w-3 h-3 flash-bolt flex-shrink-0" />
              FLASH PICK
            </div>
            <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: urgent ? "#EF444490" : "#F9731690" }}>
              {urgent ? "¡ÚLTIMOS MINUTOS!" : "PICK URGENTE"}
            </div>
          </div>

          {/* ── Center: Match info ──────────────────────── */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
              {/* Teams */}
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-black text-white text-sm truncate">{pick.home_team}</span>
                <span className="text-xs font-bold text-zinc-500">vs</span>
                <span className="font-black text-white text-sm truncate">{pick.away_team}</span>
              </div>
              {/* League badge */}
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                style={{ background: "rgba(255,255,255,0.06)", color: "#71717A" }}>
                {pick.league}
              </span>
            </div>

            {/* Pick & odds */}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#52525B" }}>
                {MARKET_LABELS[pick.market] || pick.market}
              </span>
              <span className="text-white/30 text-xs">·</span>
              {isLocked ? (
                <span
                  className="relative text-sm font-black text-white select-none"
                  style={{ filter: "blur(5px)", userSelect: "none" }}
                  aria-hidden="true"
                >
                  {/* Fake blurred text — same length as a typical pick */}
                  Real Madrid Ganador
                  <span
                    className="absolute inset-0 flex items-center gap-1.5 justify-start"
                    style={{ filter: "none" }}
                  >
                    <Lock className="w-3 h-3 flex-shrink-0" weight="fill"
                      style={{ color: urgent ? "#EF4444" : "#F97316" }} />
                    <span className="text-xs font-black" style={{ color: urgent ? "#EF4444" : "#F97316" }}>
                      Solo suscriptores
                    </span>
                  </span>
                </span>
              ) : (
                <span className="text-sm font-black text-white">{pick.pick_selection}</span>
              )}
              {pick.odds && !isLocked && (
                <span className="font-black text-sm" style={{ color: urgent ? "#EF4444" : "#F97316" }}>
                  @ {Number(pick.odds).toFixed(2)}
                </span>
              )}
            </div>
          </div>

          {/* ── Right: Countdown + close ─────────────────── */}
          <div className="flex-shrink-0 flex items-center gap-3">
            <div className="flex flex-col items-center gap-0.5">
              <DigitalClock secs={secs} />
              <div className="text-[9px] uppercase tracking-widest font-black" style={{ color: urgent ? "#EF444460" : "#F9731660" }}>
                expira en
              </div>
            </div>

            <button
              data-testid="flash-banner-close"
              onClick={(e) => {
                e.stopPropagation();
                sessionStorage.setItem(`flash_dismissed_${pick.id}`, "1");
                setDismissed(true);
              }}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:bg-white/10 flex-shrink-0"
              style={{ color: "#52525B" }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
